// src/main.tsx or index.tsx
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { ThemeProvider, CssBaseline } from "@mui/material";
import theme247mui from "Utils/theme";
import reportWebVitals from "./reportWebVitals";
import { I18nProvider } from "Utils/i18n";
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { useBoundStore } from "Zustand/store/mainStore";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance: PublicClientApplication = new PublicClientApplication(msalConfig);
/*
// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event: any) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event?.payload?.account) {
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
  }
});
*/

// Handle redirect promise on page load
msalInstance.initialize().then(() => {
  // Set the active account after redirect
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // loginRedirect's single redirect completion can emit both LOGIN_SUCCESS
  // and ACQUIRE_TOKEN_SUCCESS; this guards against running the roles-check +
  // token-exchange flow twice for the same sign-in.
  let ssoProcessing = false;

  // Listen for login events to set the active account
  msalInstance.addEventCallback((event) => {
    if ((event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) && event.payload) {
      const payload = event.payload as any;
      const account = payload.account;
      msalInstance.setActiveAccount(account);

      if (!account?.username) {
        useBoundStore.setState((state) => ({
          auth: {
            ...state.auth,
            error:
              "SSO login failed: no username was returned for this account. Please try again.",
          },
        }));
        window.location.assign("/");
        return;
      }

      if (ssoProcessing) return;
      ssoProcessing = true;

      // The account object doesn't reliably carry the ID token; it's on the
      // event payload itself (AuthenticationResult.idToken).
      const idToken = payload.idToken || account?.idToken;

      // Don't navigate to /projects until the TestVision user is confirmed
      // to have roles and the ID token has been exchanged for a session.
      useBoundStore
        .getState()
        .loginWithSso(account, idToken)
        .then((success) => {
          window.location.assign(success ? "/projects" : "/");
        })
        .finally(() => {
          ssoProcessing = false;
        });
    }
  });

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );

  root.render(
    // <React.StrictMode>
    <I18nProvider>
      <ThemeProvider theme={theme247mui}>
        <ToastContainer />
        <CssBaseline />
        <div className="harmanPaper">
          <App instance={msalInstance} />
        </div>
      </ThemeProvider>
    </I18nProvider>
    // </React.StrictMode>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();